const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for delivery pincodes to reduce API calls
let cachedPincodes = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all delivery pincodes from the API
 * @returns {Promise<Array>} Array of pincode objects
 */
export const fetchDeliveryPincodes = async () => {
  try {
    // Return cached data if it's still valid
    if (cachedPincodes && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return cachedPincodes;
    }

    const response = await fetch(`${API_URL}/api/pincodes/`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch pincodes');
    }
    
    const pincodes = await response.json();
    
    // Update cache
    cachedPincodes = pincodes;
    cacheTimestamp = Date.now();
    
    return pincodes;
  } catch (error) {
    console.error('Error fetching pincodes:', error);
    // Return empty array on error
    return [];
  }
};

/**
 * Validate if a pincode is in our delivery area (using API)
 * @param {string} pincode - 6-digit pincode
 * @returns {Promise<Object>} { valid: boolean, area: string|null }
 */
export const validatePincode = async (pincode) => {
  try {
    const response = await fetch(`${API_URL}/api/pincodes/check/${pincode.trim()}`);
    
    if (!response.ok) {
      throw new Error('Failed to validate pincode');
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error validating pincode:', error);
    return {
      valid: false,
      area: null
    };
  }
};

/**
 * Get user's location using browser geolocation API
 * @returns {Promise<Object>} { latitude, longitude, pincode }
 */
export const getUserLocation = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Use reverse geocoding to get pincode
          // Using Google Maps Geocoding API (free alternative: Nominatim)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'HouseOfMicrogreens/1.0'
              }
            }
          );
          
          const data = await response.json();
          const pincode = data.address?.postcode || null;
          
          resolve({
            latitude,
            longitude,
            pincode,
            address: data.display_name
          });
        } catch (error) {
          reject(error);
        }
      },
      (error) => {
        reject(error);
      }
    );
  });
};
